import React, { useRef } from 'react'
import { EditorState } from 'draft-js'
import api from 'helpers/api'
import { IFileParentResource } from 'types'
import { Picture, Attachment, Video } from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import Tooltip from 'components/tooltip'
import { pressable } from 'components/editor/helpers/pressable'

const MEDIA_LABELS = { image: 'Add photo', video: 'Add video', file: 'Add attachment' }

interface Props {
  editorState: EditorState
  onChange: (newEditorState: EditorState) => void
  mediaType: 'image' | 'video' | 'file'
  resources: IFileParentResource[]
  insertUploadProgress: (
    editorState: EditorState,
    mediaType: 'image' | 'video' | 'file',
    filename: string
  ) => { editorState: EditorState; entityKey: string } | null
  updateUploadProgress: (entityKey: string, progress: number) => void
  replaceUploadProgressWithMedia: (
    progressEntityKey: string,
    mediaType: 'image' | 'video' | 'file',
    mediaData: { url: string; filename?: string; extra?: any } | null
  ) => EditorState
}

const UploadMedia: React.FC<Props> = ({
  editorState,
  onChange,
  mediaType,
  resources,
  insertUploadProgress,
  updateUploadProgress,
  replaceUploadProgressWithMedia
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { displayErrorMessage } = useApplicationStore()
  const uploadEntityKeyRef = useRef<string | null>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return false
    }

    const file = e.target.files[0]

    // Insert progress placeholder into editor
    const result = insertUploadProgress(editorState, mediaType, file.name)
    if (!result) {
      // Cannot insert when selection is within an atomic block
      if (inputRef.current) {
        inputRef.current.value = ''
      }
      return
    }
    const { editorState: newEditorState, entityKey } = result
    uploadEntityKeyRef.current = entityKey
    onChange(newEditorState)

    try {
      const response = await api.uploadFileWithProgress(file, resources, (progress) => {
        updateUploadProgress(entityKey, progress)
      })

      // Get the latest editor state and replace progress with actual media
      const finalEditorState = replaceUploadProgressWithMedia(entityKey, mediaType, {
        url: response.data.url,
        filename: response.data.filename
      })

      onChange(finalEditorState)
      uploadEntityKeyRef.current = null
      // Reset the input so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    } catch (e) {
      // Remove progress placeholder on error
      if (uploadEntityKeyRef.current) {
        const cleanedState = replaceUploadProgressWithMedia(uploadEntityKeyRef.current, mediaType, null)
        onChange(cleanedState)
      }
      displayErrorMessage(e)
      uploadEntityKeyRef.current = null
      // Reset the input so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  return (
    <div className="flex cursor-pointer">
      <Tooltip content={MEDIA_LABELS[mediaType]}>
        <div
          aria-label={MEDIA_LABELS[mediaType]}
          {...pressable()}
          data-media={mediaType}
          className="flex items-center hover:bg-surface-sunken transition-all rounded cursor-pointer select-none p-[7px] mr-2.5"
          onClick={(event) => {
            event.stopPropagation()
            inputRef.current!.click()
          }}
        >
          <label className="cursor-pointer" htmlFor={'file-input-' + mediaType}>
            {mediaType === 'image' ? <Picture /> : mediaType === 'video' ? <Video /> : <Attachment />}
          </label>
          <input type="file" id="file" ref={inputRef} onChange={handleFileChange} className="hidden" />
        </div>
      </Tooltip>
    </div>
  )
}

export default UploadMedia
